import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { isAuthenticated } from '@/lib/auth';
import { apiClient } from '@/lib/api-client';

interface AnalyticsSummary {
  totalSessions: number;
  completedSessions: number;
  failedSessions: number;
  pendingSessions: number;
  totalVolume: number;
  successRate: string;
}

interface AnalyticsData {
  summary: AnalyticsSummary;
  byType: {
    ONRAMP: number;
    OFFRAMP: number;
  };
  period: {
    label: string;
    from?: string;
    to: string;
  };
}

export default function Overview() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<string>('30d');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const loadAnalytics = async () => {
      try {
        const response = await apiClient.get(`/analytics/overview?period=${period}`);
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [navigate, period]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-gray-600">Loading analytics...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>

        {/* Summary Cards */}
        {analytics && (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader>
                  <CardDescription>Total Sessions</CardDescription>
                  <CardTitle className="text-3xl">{analytics.summary.totalSessions}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    All payment sessions in selected period
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardDescription>Completed</CardDescription>
                  <CardTitle className="text-3xl text-green-600">
                    {analytics.summary.completedSessions}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Success rate: {analytics.summary.successRate}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardDescription>Failed</CardDescription>
                  <CardTitle className="text-3xl text-red-600">
                    {analytics.summary.failedSessions}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Failed or KYC failed sessions
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardDescription>Pending</CardDescription>
                  <CardTitle className="text-3xl text-yellow-600">
                    {analytics.summary.pendingSessions}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Currently in progress
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Volume Card */}
            <Card>
              <CardHeader>
                <CardTitle>Total Volume (Completed)</CardTitle>
                <CardDescription>Transaction volume in selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-blue-600">
                  {formatCurrency(analytics.summary.totalVolume)}
                </p>
              </CardContent>
            </Card>

            {/* By Type */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Onramp Sessions</CardTitle>
                  <CardDescription>Buy crypto with fiat</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{analytics.byType.ONRAMP}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Offramp Sessions</CardTitle>
                  <CardDescription>Sell crypto for fiat</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{analytics.byType.OFFRAMP}</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and next steps</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <button
                    onClick={() => navigate('/dashboard/transactions')}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="text-2xl mb-2">💳</div>
                    <div className="font-medium">View Transactions</div>
                    <div className="text-sm text-gray-600">See all sessions</div>
                  </button>

                  <button
                    onClick={() => navigate('/dashboard/api-keys')}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="text-2xl mb-2">🔑</div>
                    <div className="font-medium">Manage API Keys</div>
                    <div className="text-sm text-gray-600">Create or rotate keys</div>
                  </button>

                  <button
                    onClick={() => navigate('/dashboard/webhooks')}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="text-2xl mb-2">🔗</div>
                    <div className="font-medium">Configure Webhooks</div>
                    <div className="text-sm text-gray-600">Set up notifications</div>
                  </button>

                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="text-2xl mb-2">⚙️</div>
                    <div className="font-medium">Settings</div>
                    <div className="text-sm text-gray-600">Account & security</div>
                  </button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
